import { API_URL } from "config";
import Http from "util/https";
import apiClient from "config/apiClient";
import { downloadFromUrl } from "features/stock/utils/common";
import { Severity } from "type";
import { AxiosResponse } from "axios";
import type { BOErrorParams } from "../types";
import { removeEmptyValues } from "../utils/common";


const LIMIT = 1000000;

type StartResponseApi = {
  dataCount: number;
  partCount: number;
  process_id: string;
};

type CheckResponseApi = {
  part: number; // 終わったパートの数を返す？0~n
  status: number; // 0:作成中 1:完了 9:エラー終了
}[];
type MergeResponseApi = {
  process_id: string;
  url: string;
  expired: number; // デフォルト900秒
};
/**
 * システム利用レポートCSVをダウンロードするAPI
 * @param params システムレポート取得パラメータ
 * @param showAlert アラート表示
 * @returns Blob データ
 */
export default async function downloadBOErrorApi(
  params: BOErrorParams,
  showAlert: (msg: string, severity?: Severity) => void
): Promise<void> {
  // const response = await apiClient.post<Blob>(
  //   "report/system-usage/download",
  //   {
  //     store_name: params.storeName,
  //     start_date: params.startDate,
  //     end_date: params.endDate,
  //   },
  //   { responseType: "blob" },
  // );

  const http = Http.default.alterAuth({ useToken: true });
  const urlStart = `${API_URL}/error/report/csv/download/start/export`;
  const urlExport = `${API_URL}/error/report/csv/download/export/part`;
  let res: AxiosResponse<StartResponseApi, any>;
  try {
    res = await http.post<StartResponseApi>(urlStart, removeEmptyValues({
      // store_name: params.storeName, // ToDo: store_cdに変更。
      // report_type: 7,
      // start_date: params.startDate,
      // end_date: params.endDate,
      // limit: 10000,

      datetime_from: params.dateFrom,
      datetime_to: params.dateTo,
      // store_cd: params.storeCd,
      limit: LIMIT,
    }));
  } catch (err) {
    console.error(err);
    showAlert('CSVダウンロード開始でエラーが発生しました。', 'error');
    throw new Error('');
  }

  if (!res.data || !res.data.process_id) {
    showAlert(`CSVダウンロード開始でエラーが発生しました。`, 'error');
    if (res.data && !res.data.process_id)
      console.error(`CSVダウンロード開始responseエラー。job_idが${res.data.process_id}です。`);
    else if (res.data && !res.data.partCount)
      console.error(`CSVダウンロード開始responseエラー。total_partが${res.data.partCount}です。`);
    else console.error(`CSVダウンロード開始responseエラー。dataが${res.data}です。`);
    throw new Error('');
  }

  const processId = res.data.process_id; // ジョブID
  const totalParts = res.data.partCount; // 何回ダウンロードするか

  // exportAllParts
  const promises = [];
  if (totalParts <= 0) {
    showAlert('ダウンロードするデータが0件でした。ダウンロードを中断します。', 'error');
    console.error('ダウンロード件数が0件終了');
    return;
    // promises.push(
    //   http.post<null>(urlExport, {
    //     report_kind: 7,
    //     target: params.target,
    //     limit: LIMIT,
    //     company_cd: params.companyCd,
    //     store_cd: params.storeCd,
    //     process_id: processId,
    //     part: 0,
    //   }),
    // )
  }
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < totalParts; i++) {
    promises.push(
      http.post<null>(urlExport, removeEmptyValues({
        datetime_from: params.dateFrom,
        datetime_to: params.dateTo,
        limit: LIMIT,
        // store_cd: params.storeCd,
        process_id: processId,
        part: i,
      })),
    );
  }
  await Promise.all(promises);

  // pollStatus
  const maxRetry = 30; // 最大リトライ回数
  let retry = 30; // 最大リトライ回数
  const ms = 3000; // interval間隔
  let errorCheckFlag = false; // 各パートの処理完了後エラーが1つでも含まれていればtrue

  while (retry > 0) {
    const urlCheck = `${API_URL}/error/report/csv/download/check/part/status`;
    try {
      // eslint-disable-next-line no-await-in-loop
      const resCheck = await http.post<CheckResponseApi>(urlCheck, {process_id: processId});
      if (resCheck.data.every(d => d.status !== 0)) { // 処理中のものがなければループ脱出
        if (!(resCheck.data.every(d => d.status === 1))) { // 処理がすべて完了でなければ失敗
          console.error('ダウンロードチェックエラー内容 resCheck.data:', resCheck.data, "process_id:", processId);
          errorCheckFlag = true;
        }
        break;
      } else {
        retry -= 1;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {setTimeout(resolve, ms)}); // 待機
      }
    } catch (err) {
      console.error(err);
      retry -= 1;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {setTimeout(resolve, ms)}); // 待機
    }
  }
  if (retry <= 0) {
    showAlert('リトライ回数エラーです。', 'error');
    console.log(`リトライ回数エラーです。${(maxRetry * ms) / 1000}秒リトライしましたがCSV出力が完了しませんでした。`);
    throw new Error('');
  }
  if (errorCheckFlag) {
    showAlert('ダウンロードチェックエラーです。', 'error');
    console.error(`ダウンロード処理でエラーのパートがあります。詳細はresCheck.dataをご確認ください。`);
    throw new Error('');
  }

  // merge
  const urlMerge = `${API_URL}/error/report/csv/download/merge/csv`;
  let resMerge: AxiosResponse<MergeResponseApi, any>;
  try {
    resMerge = await http.post<MergeResponseApi>(urlMerge, {
      process_id: processId,
      part: totalParts,
    });
  } catch (err) {
    console.error(err);
    showAlert('CSVのマージに失敗しました。', 'error');
    throw new Error('');
  }

  if (!resMerge || !resMerge.data || !resMerge.data.url) {
    showAlert('CSVのマージのレスポンスが不正です。', 'error');
    if (resMerge.data && !resMerge.data.url)
      console.error(`CSVマージresponseエラー。urlが${resMerge.data.url}です。`);
    else if (!resMerge) console.error(`CSVマージresponseエラー。resMergeが${resMerge}です。`);
    else console.error(`CSVマージresponseエラー。resMerge.dataが${resMerge.data}です。`);
    throw new Error('');
  }

  await downloadFromUrl(resMerge.data.url, "system_usage_report_");
  // if (!response.data) throw new Error("No data");

  // return response.data;
}
